/**
 * Small, defensive helpers for rendering the free-form `detail: Dict[str, Any]`
 * blob each `FnoComponentScore` carries (see backend `scoring.py`'s
 * `ComponentResult.detail` — genuinely untyped on the backend, by design,
 * since it's a diagnostic breakdown not a contract). Rather than assuming a
 * specific nested shape per component (which would silently break the UI
 * the moment a backend detail key is renamed/added), `flattenDetail` walks
 * one level of nesting and turns every leaf into a human-readable
 * "label: value" row — this is what backs the "Trend & Momentum
 * Diagnostics" accordion's generic detail rendering, and the
 * "Data Staleness & Risk Warnings" accordion's transparency requirement
 * that every disclosed backend limitation surfaces, not just the ones this
 * component happens to know the name of.
 */

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function humanizeKey(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatLeaf(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return Number.isInteger(value) ? String(value) : value.toFixed(2);
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}

export interface DetailRow {
  label: string;
  value: string;
}

/** Flattens a detail object up to two levels deep (group -> field) into
 * a flat list of display rows, e.g. `{ ema_alignment: { points: 10 } }` ->
 * `[{ label: "Ema Alignment · Points", value: "10" }]`. */
export function flattenDetail(detail: Record<string, unknown> | undefined | null): DetailRow[] {
  if (!detail) return [];
  const rows: DetailRow[] = [];
  for (const [key, value] of Object.entries(detail)) {
    if (isRecord(value)) {
      for (const [innerKey, innerValue] of Object.entries(value)) {
        if (isRecord(innerValue)) continue; // cap at 2 levels — avoids unbounded recursion on unexpected shapes
        rows.push({ label: `${humanizeKey(key)} · ${humanizeKey(innerKey)}`, value: formatLeaf(innerValue) });
      }
    } else {
      rows.push({ label: humanizeKey(key), value: formatLeaf(value) });
    }
  }
  return rows;
}

export function getNested(detail: Record<string, unknown> | undefined | null, ...path: string[]): unknown {
  let current: unknown = detail;
  for (const key of path) {
    if (!isRecord(current)) return undefined;
    current = current[key];
  }
  return current;
}

export function asNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export function asString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}
